import { PermissionEnum, TaskTypeEnum } from '@sr-sdks/api-sdk-axios';
import { lazy } from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router-dom';
import { prefetchWorkspace, prefetchWorkspaces } from '../api/prefetch';
import { EmbedView } from '../components/EmbedView';
import ErrorPage from '../components/ErrorPage';
import { MigrationSwitch } from '../components/MigrationSwitch';
import { NavigateWithParams } from '../components/NavigateWithParams';
import { WorkspaceRedirect } from '../components/WorkspaceRedirect';
import { FEATURE_FLAGS, MSR3_HOME_FLAGS } from '../constant/featureFlags';
import { MSR3BaseLayout } from '../layout/MSR3BaseLayout';
import ManageWorkspaceTabLayout from '../layout/ManageWorkspaceTabLayout';
import { RoyaltiesLayout } from '../layout/RoyaltiesLayout';
import { RoyaltiesMsr3RegionLayout } from '../layout/RoyaltiesMsr3RegionLayout';
import WorkspaceLayout from '../layout/WorkspaceLayout';
import { WorkspaceSettingsLayout } from '../layout/WorkspaceSettingsLayout';
import {
  royaltiesAnzLayoutConfig,
  royaltiesWorldLayoutConfig,
} from '../layout/royaltiesLayoutConfig';
import Clients from '../pages/Workspace/Clients';
import Payments from '../pages/Workspace/Payments';
import Titles from '../pages/Workspace/Titles';
import { ClientsV3 } from '../pages/v3/Client/ClientsV3';
import { ClientSummaryV3 } from '../pages/v3/Client/Summary/ClientSummaryV3';
import { Home } from '../pages/v3/Home';
import { Disbursements } from '../pages/v3/Payments/Disbursements';
import { Residuals } from '../pages/v3/Payments/Residuals';
import { RoyaltiesPaidRoyalties } from '../pages/v3/Payments/RoyaltiesPaidRoyalties';
import { RoyaltiesStatementSummaries } from '../pages/v3/Payments/RoyaltiesStatementSummaries';
import { RoyaltiesStatements } from '../pages/v3/Payments/RoyaltiesStatements';
import {
  RoyaltiesAnzEarningsStatements,
  RoyaltiesAnzTitleStatements,
  RoyaltiesWorldEarningsStatements,
  RoyaltiesWorldTitleStatements,
} from '../pages/v3/Payments/RoyaltiesTabPage';
import { BankAccount } from '../pages/v3/Settings/BankAccount';
import { Details } from '../pages/v3/Settings/Details';
import { Teammates } from '../pages/v3/Settings/Teammates';
import { TasksLayout } from '../pages/v3/Tasks/TasksLayout';
import { TasksTabPage } from '../pages/v3/Tasks/TasksTabPage';
import { AddTitleV3 } from '../pages/v3/Title/AddTitle/AddTitleV3';
import { TitleSummaryV3 } from '../pages/v3/Title/Summary/TitleSummaryV3';
import { TitlesV3 } from '../pages/v3/Title/TitlesV3';
import { ManageWorkspacePermissions } from '../types/workspaceTypes';
import {
  hasDisbursementsEnabled,
  hasResidualsEnabled,
} from '../utils/helpers/common';
import { ProtectLazyLoadRoute } from './LazyLoadElement';
import { PaymentsRedirect } from './PaymentsRedirect';
import ProtectRoute from './ProtectRoute';
import { RequireAddonEnabled } from './RequireAddonEnabled';
import { RequireAgentForHistoricRoyalties } from './RequireAgentForHistoricRoyalties';
import { RequireCustomerViewForStaff } from './RequireCustomerViewForStaff';
import {
  RequireRoyaltiesAccess,
  RequireRoyaltiesAnzAccess,
  RequireRoyaltiesWorldAccess,
} from './RequireRoyaltiesAccess';
import { RequireRoyaltiesRegistration } from './RequireRoyaltiesRegistration';

export const WorkspaceRoutes: RouteObject[] = [
  {
    element: (
      <ProtectRoute
        onAuthorised={(workspaceId) => {
          if (workspaceId) prefetchWorkspace(workspaceId);
          prefetchWorkspaces();
        }}
      >
        <RequireCustomerViewForStaff>
          <WorkspaceRedirect />
        </RequireCustomerViewForStaff>
      </ProtectRoute>
    ),
    loader: () => {
      return {
        permissionRequired: [PermissionEnum.ViewWorkspace],
      };
    },
    children: [
      {
        index: true,
        element: (
          <MigrationSwitch
            featureFlags={MSR3_HOME_FLAGS}
            newComponent={<NavigateWithParams replace to="home" />}
            oldComponent={<NavigateWithParams replace to="titles" />}
          />
        ),
      },
      {
        path: 'home',
        element: (
          <MigrationSwitch
            featureFlags={MSR3_HOME_FLAGS}
            newComponent={
              <ProtectRoute permissionRequired={[PermissionEnum.ViewWorkspace]}>
                <Home />
              </ProtectRoute>
            }
            oldComponent={<NavigateWithParams replace to="titles" />}
          />
        ),
        loader: () => ({
          permissionRequired: [PermissionEnum.ViewWorkspace],
          title: 'Home',
        }),
      },
      {
        path: 'tasks',
        element: (
          <MigrationSwitch
            featureFlags={[
              FEATURE_FLAGS.MSR3_ENABLED,
              FEATURE_FLAGS.MSR3_TASKS_ROUTE,
            ]}
            newComponent={
              <ProtectRoute permissionRequired={[PermissionEnum.ViewTasks]}>
                <TasksLayout />
              </ProtectRoute>
            }
            oldComponent={<ErrorPage notFound />}
          />
        ),
        loader: () => ({
          permissionRequired: [PermissionEnum.ViewTasks],
          title: 'Tasks',
        }),
        children: [
          {
            index: true,
            element: <NavigateWithParams replace to="potential" />,
          },
          {
            path: 'potential',
            element: (
              <TasksTabPage
                key="potential"
                type={TaskTypeEnum.PotentialClaim}
              />
            ),
          },
          {
            path: 'competing',
            element: (
              <TasksTabPage
                key="competing"
                type={TaskTypeEnum.CompetingClaim}
              />
            ),
          },
          {
            path: 'drafts',
            element: (
              <TasksTabPage
                key="drafts"
                type={TaskTypeEnum.DraftRegistration}
              />
            ),
          },
        ],
      },
      {
        path: 'titles',
        element: <Outlet />,
        loader: () => ({
          permissionRequired: [PermissionEnum.ViewTitles],
          title: 'Titles',
        }),
        children: [
          {
            // MSR3 children render their own MSR3BaseLayout; MSR2 keeps WorkspaceLayout.
            element: (
              <MigrationSwitch
                featureFlags={FEATURE_FLAGS.MSR3_TITLE_PAGE_ROUTE}
                newComponent={<Outlet />}
                oldComponent={<WorkspaceLayout />}
              />
            ),
            children: [
              {
                index: true,
                element: (
                  <MigrationSwitch
                    featureFlags={FEATURE_FLAGS.MSR3_TITLE_PAGE_ROUTE}
                    newComponent={
                      <ProtectRoute
                        permissionRequired={[PermissionEnum.ViewTitles]}
                      >
                        <TitlesV3 />
                      </ProtectRoute>
                    }
                    oldComponent={<Titles />}
                  />
                ),
              },
            ],
          },
          {
            // `summary/:titleId` is a sibling of the titles-list layout switch
            // above, not its descendant (the `titles` route itself renders a
            // bare Outlet), so nothing upstream already renders WorkspaceLayout
            // for this route — this switch must decide the shell itself. It has
            // to require BOTH flags, matching the content switch below: only
            // when the whole titles+summary experience is on MSR3 does
            // TitleSummaryV3 (bringing its own MSR3BaseLayout) take over: any
            // other combination falls back to the legacy WorkspaceLayout shell,
            // otherwise the old TitleSummary page would render unshelled, or
            // TitleSummaryV3's shell would render nested inside WorkspaceLayout.
            path: 'summary/:titleId',
            element: (
              <MigrationSwitch
                featureFlags={[
                  FEATURE_FLAGS.MSR3_TITLE_PAGE_ROUTE,
                  FEATURE_FLAGS.MSR3_TITLE_SUMMARY_ROUTE,
                ]}
                newComponent={<Outlet />}
                oldComponent={<WorkspaceLayout />}
              />
            ),
            loader: () => ({ title: 'Title Summary' }),
            children: [
              {
                element: (
                  <MigrationSwitch
                    featureFlags={[
                      FEATURE_FLAGS.MSR3_TITLE_PAGE_ROUTE,
                      FEATURE_FLAGS.MSR3_TITLE_SUMMARY_ROUTE,
                    ]}
                    newComponent={
                      <ProtectRoute
                        permissionRequired={[PermissionEnum.ViewTitles]}
                      >
                        <TitleSummaryV3 />
                      </ProtectRoute>
                    }
                    oldComponent={ProtectLazyLoadRoute(
                      lazy(
                        () => import('../pages/Workspace/Title/TitleSummary'),
                      ),
                    )}
                  />
                ),
                // Permission gate sits on the route that renders the protected
                // component — ProtectRoute reads it via useLoaderData().
                loader: () => ({
                  permissionRequired: [PermissionEnum.ViewTitles],
                  title: 'Title Summary',
                }),
                children: [
                  {
                    index: true,
                    element: <Navigate replace to="payments" />,
                  },
                  {
                    path: 'payments',
                    element: ProtectLazyLoadRoute(
                      lazy(() => import('../pages/Workspace/Title/Payments')),
                    ),
                    loader: () => ({
                      permissionRequired: [PermissionEnum.ViewCascades],
                      title: 'Title Summary',
                    }),
                  },
                  {
                    path: 'activity-statements',
                    element: ProtectLazyLoadRoute(
                      lazy(
                        () =>
                          import('../pages/Workspace/Title/ActivityStatement'),
                      ),
                    ),
                    loader: () => ({
                      permissionRequired: [PermissionEnum.ViewCascades],
                      title: 'Title Summary',
                    }),
                  },
                  {
                    path: 'marketing-agreements',
                    element: ProtectLazyLoadRoute(
                      lazy(
                        () =>
                          import('../pages/Workspace/Title/MarketingAgreements'),
                      ),
                    ),
                    loader: () => ({
                      permissionRequired: [PermissionEnum.ViewCascades],
                      title: 'Title Summary',
                    }),
                  },
                  {
                    path: 'sales-reports',
                    element: ProtectLazyLoadRoute(
                      lazy(
                        () => import('../pages/Workspace/Title/SalesReport'),
                      ),
                    ),
                    loader: () => ({
                      permissionRequired: [PermissionEnum.ViewCascades],
                      title: 'Title Summary',
                    }),
                  },
                  {
                    path: 'beneficiary-access',
                    element: ProtectLazyLoadRoute(
                      lazy(
                        () =>
                          import('../pages/Workspace/Title/BeneficiaryAccess'),
                      ),
                    ),
                    loader: () => ({
                      title: 'Title Summary',
                    }),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'potential-claim/:resourceId',
        element: (
          <MSR3BaseLayout>
            <RequireRoyaltiesRegistration>
              <EmbedView
                embedType="potential-claim"
                loadingMessage="Loading your claim information, please stand by..."
              />
            </RequireRoyaltiesRegistration>
          </MSR3BaseLayout>
        ),
      },
      {
        path: 'competing-claim/:resourceId',
        element: (
          <MSR3BaseLayout>
            <EmbedView
              embedType="competing-claim"
              loadingMessage="Loading your claim information, please stand by..."
            />
          </MSR3BaseLayout>
        ),
      },
      {
        path: 'draft-registration/:resourceId',
        element: (
          <MSR3BaseLayout>
            <RequireRoyaltiesRegistration>
              <EmbedView
                embedType="title"
                loadingMessage="Loading your draft registration information, please stand by..."
              />
            </RequireRoyaltiesRegistration>
          </MSR3BaseLayout>
        ),
      },
      {
        path: 'add-title',
        element: (
          <ProtectRoute permissionRequired={[PermissionEnum.ViewTitles]}>
            <AddTitleV3 />
          </ProtectRoute>
        ),
        loader: () => ({
          permissionRequired: [PermissionEnum.ViewTitles],
          title: 'Add Title',
        }),
      },
      {
        path: 'payments',
        element: (
          <MigrationSwitch
            featureFlags={FEATURE_FLAGS.MSR3_PAYMENTS_ROUTE}
            newComponent={
              <ProtectRoute>
                <PaymentsRedirect />
              </ProtectRoute>
            }
            oldComponent={
              <ProtectRoute>
                <WorkspaceLayout />
              </ProtectRoute>
            }
          />
        ),
        loader: () => ({
          permissionRequired: [PermissionEnum.ViewPaymentStatements],
          title: 'Payments',
        }),
        children: [
          {
            index: true,
            element: <Payments />,
          },
        ],
      },
      {
        path: 'disbursements',
        element: (
          <MigrationSwitch
            featureFlags={FEATURE_FLAGS.MSR3_PAYMENTS_ROUTE}
            newComponent={
              <ProtectRoute>
                <RequireAddonEnabled isEnabled={hasDisbursementsEnabled}>
                  <Disbursements />
                </RequireAddonEnabled>
              </ProtectRoute>
            }
            oldComponent={<ErrorPage notFound />}
          />
        ),
        loader: () => ({
          permissionRequired: [PermissionEnum.ViewPaymentStatements],
          title: 'Disbursements',
        }),
      },
      {
        path: 'residuals',
        element: (
          <MigrationSwitch
            featureFlags={FEATURE_FLAGS.MSR3_PAYMENTS_ROUTE}
            newComponent={
              <ProtectRoute>
                <RequireAddonEnabled isEnabled={hasResidualsEnabled}>
                  <Residuals />
                </RequireAddonEnabled>
              </ProtectRoute>
            }
            oldComponent={<ErrorPage notFound />}
          />
        ),
        loader: () => ({
          permissionRequired: [PermissionEnum.ViewPaymentStatements],
          title: 'Residuals',
        }),
      },
      {
        path: 'royalties',
        element: (
          <MigrationSwitch
            featureFlags={FEATURE_FLAGS.MSR3_PAYMENTS_ROUTE}
            newComponent={
              <ProtectRoute>
                <RequireRoyaltiesAccess>
                  <RoyaltiesLayout />
                </RequireRoyaltiesAccess>
              </ProtectRoute>
            }
            oldComponent={<ErrorPage notFound />}
          />
        ),
        loader: () => ({
          permissionRequired: [PermissionEnum.ViewPaymentStatements],
          title: 'Historic Royalties',
        }),
        children: [
          {
            index: true,
            // Preserve ?v= so MigrationSwitch keeps the MSR3 override after redirect.
            element: <NavigateWithParams replace to="statements" />,
          },
          {
            path: 'statements',
            element: <RoyaltiesStatements />,
          },
          {
            // Agent-only tab: hidden from non-agents in the nav and guarded
            // here so it can't be reached by URL either.
            path: 'statement-summaries',
            element: (
              <RequireAgentForHistoricRoyalties>
                <RoyaltiesStatementSummaries />
              </RequireAgentForHistoricRoyalties>
            ),
          },
          {
            // Flagged separately: embeds MSR2's embed/royalties route, which
            // ships under APP-2523. Flag off sends the URL to Past Statements.
            path: 'paid-royalties',
            element: (
              <MigrationSwitch
                featureFlags={FEATURE_FLAGS.MSR3_PAID_ROYALTIES_TAB}
                newComponent={<RoyaltiesPaidRoyalties />}
                oldComponent={<NavigateWithParams replace to="../statements" />}
              />
            ),
          },
        ],
      },
      {
        path: 'royalties-anz',
        element: (
          <MigrationSwitch
            featureFlags={[
              FEATURE_FLAGS.MSR3_PAYMENTS_ROUTE,
              FEATURE_FLAGS.MSR3_ROYALTIES_ANZ_ROUTE,
            ]}
            newComponent={
              <ProtectRoute>
                <RequireRoyaltiesAnzAccess>
                  <RoyaltiesMsr3RegionLayout {...royaltiesAnzLayoutConfig} />
                </RequireRoyaltiesAnzAccess>
              </ProtectRoute>
            }
            oldComponent={<ErrorPage notFound />}
          />
        ),
        // Entire subtree requires Current Member + the ANZ royalties addon
        // (RequireRoyaltiesAnzAccess) — a World-only workspace must
        // not reach a table that can only ever be empty for it. Both tabs are
        // open to every workspace that clears it: workspace type is not part of
        // royalties access (APP-2370).
        // Agency workspace type alone does not grant access — see terminology
        // doc "Agent workspaces and Current Member".
        loader: () => ({
          permissionRequired: [PermissionEnum.ViewPaymentStatements],
          title: 'Royalties ANZ',
        }),
        children: [
          {
            index: true,
            // Preserve ?v= so MigrationSwitch keeps the MSR3 override after redirect.
            // Plain <Navigate> drops search params and can fall through to
            // oldComponent (not-found) when the ANZ flag is still off.
            element: <NavigateWithParams replace to="title-statements" />,
          },
          {
            path: 'title-statements',
            element: <RoyaltiesAnzTitleStatements />,
          },
          {
            path: 'earnings-statements',
            element: <RoyaltiesAnzEarningsStatements />,
          },
        ],
      },
      {
        path: 'royalties-world',
        element: (
          <MigrationSwitch
            featureFlags={[
              FEATURE_FLAGS.MSR3_PAYMENTS_ROUTE,
              FEATURE_FLAGS.MSR3_ROYALTIES_WORLD_ROUTE,
            ]}
            newComponent={
              <ProtectRoute>
                <RequireRoyaltiesWorldAccess>
                  <RoyaltiesMsr3RegionLayout {...royaltiesWorldLayoutConfig} />
                </RequireRoyaltiesWorldAccess>
              </ProtectRoute>
            }
            oldComponent={<ErrorPage notFound />}
          />
        ),
        // Entire subtree requires Current Member + royaltiesWorld addon
        // (RequireRoyaltiesWorldAccess). Both tabs are open to every workspace
        // that clears it — same policy as ANZ (APP-2370).
        loader: () => ({
          permissionRequired: [PermissionEnum.ViewPaymentStatements],
          title: 'Royalties World',
        }),
        children: [
          {
            index: true,
            element: <NavigateWithParams replace to="title-statements" />,
          },
          {
            path: 'title-statements',
            element: <RoyaltiesWorldTitleStatements />,
          },
          {
            path: 'earnings-statements',
            element: <RoyaltiesWorldEarningsStatements />,
          },
        ],
      },
      {
        path: 'clients',
        element: (
          <MigrationSwitch
            featureFlags={FEATURE_FLAGS.MSR3_CLIENT_PAGE_ROUTE}
            // MSR3 children render their own MSR3BaseLayout; MSR2 keeps WorkspaceLayout.
            newComponent={<Outlet />}
            oldComponent={
              <ProtectRoute>
                <WorkspaceLayout />
              </ProtectRoute>
            }
          />
        ),
        loader: () => ({
          permissionRequired: [PermissionEnum.ViewClients],
          title: 'Clients',
        }),
        children: [
          {
            index: true,
            element: (
              <MigrationSwitch
                featureFlags={FEATURE_FLAGS.MSR3_CLIENT_PAGE_ROUTE}
                newComponent={
                  <ProtectRoute
                    permissionRequired={[PermissionEnum.ViewClients]}
                  >
                    <ClientsV3 />
                  </ProtectRoute>
                }
                oldComponent={<Clients />}
              />
            ),
          },
          {
            // No MSR2 client summary page exists (unlike titles/summary), so
            // there's nothing for `oldComponent` to shell — either flag off
            // just returns to the clients list. Both flags required: the
            // summary page makes no sense unless the list itself is MSR3.
            path: 'summary/:clientId',
            element: (
              <MigrationSwitch
                featureFlags={[
                  FEATURE_FLAGS.MSR3_CLIENT_PAGE_ROUTE,
                  FEATURE_FLAGS.MSR3_CLIENT_SUMMARY_ROUTE,
                ]}
                newComponent={
                  <ProtectRoute
                    permissionRequired={[PermissionEnum.ViewClients]}
                  >
                    <ClientSummaryV3 />
                  </ProtectRoute>
                }
                oldComponent={<NavigateWithParams replace to=".." />}
              />
            ),
            loader: () => ({
              permissionRequired: [PermissionEnum.ViewClients],
              title: 'Client Summary',
            }),
          },
        ],
      },
      {
        path: 'manage-workspace',
        element: (
          <MigrationSwitch
            featureFlags={FEATURE_FLAGS.MSR3_SETTINGS_ROUTE}
            newComponent={
              <ProtectRoute>
                <WorkspaceSettingsLayout />
              </ProtectRoute>
            }
            oldComponent={
              <ProtectRoute>
                <WorkspaceLayout />
              </ProtectRoute>
            }
          />
        ),
        loader: () => ({
          permissionRequired: ManageWorkspacePermissions,
          title: 'Settings',
        }),
        children: [
          {
            index: true,
            element: <Navigate replace to="details" />,
          },
          {
            path: 'details',
            element: (
              <MigrationSwitch
                featureFlags={FEATURE_FLAGS.MSR3_SETTINGS_ROUTE}
                newComponent={
                  <ProtectRoute
                    permissionRequired={[PermissionEnum.EditWorkspaceDetails]}
                  >
                    <Details />
                  </ProtectRoute>
                }
                oldComponent={
                  <ProtectRoute
                    permissionRequired={[PermissionEnum.EditWorkspaceDetails]}
                  >
                    <ManageWorkspaceTabLayout />
                  </ProtectRoute>
                }
              />
            ),
            loader: () => ({
              permissionRequired: [PermissionEnum.EditWorkspaceDetails],
            }),
            children: [
              {
                index: true,
                element: ProtectLazyLoadRoute(
                  lazy(
                    () => import('../pages/Workspace/ManageWorkspace/Details'),
                  ),
                ),
              },
            ],
          },
          {
            path: 'bank-account',
            element: (
              <MigrationSwitch
                featureFlags={FEATURE_FLAGS.MSR3_SETTINGS_ROUTE}
                newComponent={
                  <ProtectRoute
                    permissionRequired={[PermissionEnum.EditBankDetails]}
                  >
                    <BankAccount />
                  </ProtectRoute>
                }
                oldComponent={
                  <ProtectRoute
                    permissionRequired={[PermissionEnum.EditBankDetails]}
                  >
                    <ManageWorkspaceTabLayout />
                  </ProtectRoute>
                }
              />
            ),
            loader: () => ({
              permissionRequired: [PermissionEnum.EditBankDetails],
            }),
            children: [
              {
                index: true,
                element: ProtectLazyLoadRoute(
                  lazy(
                    () =>
                      import('../pages/Workspace/ManageWorkspace/BankAccountV2'),
                  ),
                ),
              },
            ],
          },
          {
            path: 'teammates',
            element: (
              <MigrationSwitch
                featureFlags={FEATURE_FLAGS.MSR3_SETTINGS_ROUTE}
                newComponent={
                  <ProtectRoute
                    permissionRequired={[PermissionEnum.EditTeammates]}
                  >
                    <Teammates />
                  </ProtectRoute>
                }
                oldComponent={
                  <ProtectRoute
                    permissionRequired={[PermissionEnum.EditTeammates]}
                  >
                    <ManageWorkspaceTabLayout />
                  </ProtectRoute>
                }
              />
            ),
            loader: () => ({
              permissionRequired: [PermissionEnum.EditTeammates],
            }),
            children: [
              {
                index: true,
                element: ProtectLazyLoadRoute(
                  lazy(
                    () =>
                      import('../pages/Workspace/ManageWorkspace/Teammates'),
                  ),
                ),
              },
            ],
          },
        ],
      },
    ],
  },
];
